import { validate as isUuid } from "uuid"
import { store } from "@/core/store"
import type { MediaAccess } from "../interface/media"
import { postBotSessionIdentity } from "../utils/sessionIdentity"
import { postBotTransport } from "./publications/api"
import { createMediaAccessOwner } from "./mediaAccess"

/** A direct base query, NOT an RTK endpoint. In particular, never return its
 * successful payload/meta through dispatch, a query cache, or a logging action. */
export async function requestMediaAccess(
  id: string,
  signal: AbortSignal,
): Promise<MediaAccess> {
  if (!isUuid(id)) throw new TypeError("Media ID must be a UUID")
  if (!postBotSessionIdentity(store.getState().auth))
    throw Object.assign(new Error("Media access requires authentication"), {
      status: 401,
    })
  const controller = new AbortController()
  const abort = () => controller.abort()
  signal.addEventListener("abort", abort, { once: true })
  if (signal.aborted) abort()
  try {
    const result = await postBotTransport(
      {
        url: `media/${id}/access`,
        method: "GET",
        cache: "no-store",
        timeout: 15_000,
      },
      {
        signal: controller.signal,
        abort,
        dispatch: store.dispatch,
        getState: store.getState,
        extra: undefined,
        endpoint: "mediaAccess",
        type: "query",
      },
      {},
    )
    // Do not retain a server payload (or fetch metadata) in an error either.
    if (result.error)
      throw Object.assign(new Error("Media access failed"), {
        status: (result.error as { status?: unknown }).status,
      })
    return result.data as MediaAccess
  } finally {
    signal.removeEventListener("abort", abort)
  }
}

export const mediaAccessOwner = createMediaAccessOwner(requestMediaAccess)
const resume = () =>
  mediaAccessOwner.setVisible(document.visibilityState !== "hidden")
document.addEventListener("visibilitychange", resume)
window.addEventListener("pageshow", resume)
window.addEventListener("focus", resume)
resume()
